import { z } from 'zod';
import { zodiacSignEnum } from '../zodiac';

export const MAX_CONVERSATION_CONTENT_LENGTH = 1000;

export const natalChartDataDebugSchema = z
  .object({
    planets: z.array(
      z.object({
        name: z.string(),
        sign: zodiacSignEnum,
        house: z.number(),
      })
    ),
    ascendant: z.object({ sign: zodiacSignEnum }),
    insights: z.object({
      dominantElements: z.array(z.object({ element: z.string() })).min(1),
      dominantModes: z.array(z.object({ mode: z.string() })).min(1),
    }),
  })
  .nullable();

export type NatalChartDataDebug = z.infer<typeof natalChartDataDebugSchema>;

export const dailyCosmicWeatherSchema = z.object({
  date: z.string(),
  moonPhase: z.object({
    name: z.string(),
    illumination: z.number(),
    emoji: z.string(),
  }),
  mercuryStatus: z.object({
    isRetrograde: z.boolean(),
    sign: z.string(),
    message: z.string(),
  }),
  significantAspects: z.array(
    z.object({
      planets: z.array(z.string()),
      aspect: z.string(),
      description: z.string(),
    })
  ),
  planetaryFocus: z.array(
    z.object({
      planet: z.string(),
      sign: z.string(),
      isRetrograde: z.boolean(),
      description: z.string(),
    })
  ),
  summary: z.string(),
  message: z.string().optional(),
  recommendations: z.array(z.string()).optional(),
});

export type DailyCosmicWeatherPayload = z.infer<typeof dailyCosmicWeatherSchema>;

export const oracleConversationMessageSchema = z.object({
  role: z.enum(['user', 'oracle']),
  content: z.string().max(MAX_CONVERSATION_CONTENT_LENGTH),
});

export type OracleConversationMessage = z.infer<typeof oracleConversationMessageSchema>;

const MIN_NATAL_PANEL_SUMMARY_LENGTH = 60;
const MIN_NATAL_PANEL_FACTORS_COUNT = 1;
const MAX_NATAL_PANEL_FACTORS_COUNT = 8;
const MIN_NATAL_PANEL_ADVICE_COUNT = 2;
const MAX_NATAL_PANEL_ADVICE_COUNT = 4;
const MAX_DAILY_VIBE_SCORE = 100;

const natalPanelSchema = z.object({
  title: z.string(),
  summary: z.string().min(MIN_NATAL_PANEL_SUMMARY_LENGTH),
  factors: z
    .array(z.string())
    .min(MIN_NATAL_PANEL_FACTORS_COUNT)
    .max(MAX_NATAL_PANEL_FACTORS_COUNT),
  advice: z.array(z.string()).min(MIN_NATAL_PANEL_ADVICE_COUNT).max(MAX_NATAL_PANEL_ADVICE_COUNT),
});

export const NatalSynthesisSchema = z.object({
  career: natalPanelSchema,
  health: natalPanelSchema,
  relationships: natalPanelSchema,
  love: natalPanelSchema,
  friendship: natalPanelSchema,
  strengths: natalPanelSchema,
  weaknesses: natalPanelSchema,
});

export type NatalSynthesis = z.infer<typeof NatalSynthesisSchema>;

// --- Horoscope (Unified Daily / Weekly / Monthly) ---
export const DailyHoroscopeUnifiedSchema = z.object({
  headline: z.string(),
  vibeScore: z.number().min(0).max(MAX_DAILY_VIBE_SCORE),
  keywords: z.array(z.string()),
  mainRead: z.string(),
  categories: z.object({
    love: z.string(),
    career: z.string(),
    wellness: z.string(),
  }),
  dashboardWidgets: z.object({
    cosmicMatch: z.string(),
    luckyNumber: z.number(),
    powerColor: z.string(),
    mantra: z.string(),
  }),
});

export type DailyHoroscopeUnified = z.infer<typeof DailyHoroscopeUnifiedSchema>;

export const WeeklyHoroscopeSchema = z.object({
  mantra: z.string(),
  overview: z.string(),
  powerDay: z.string(),
  challengeDay: z.string(),
  scopes: z.object({
    love: z.string(),
    career: z.string(),
    spiritual: z.string(),
  }),
});

export type WeeklyHoroscope = z.infer<typeof WeeklyHoroscopeSchema>;

export const MonthlyHoroscopeSchema = z.object({
  theme: z.string(),
  keyDates: z.array(z.string()),
  content: z.object({
    general: z.string(),
    love: z.string(),
    career: z.string(),
  }),
});

export type MonthlyHoroscope = z.infer<typeof MonthlyHoroscopeSchema>;

// --- Personal Portrait ---

const MIN_SECTION_TEXT_LENGTH = 120;
const MIN_EVIDENCE_EXPLANATION_LENGTH = 30;
const MIN_FACTORS_COUNT = 1;
const MAX_FACTORS_COUNT = 8;
const MIN_EVIDENCES_PER_SECTION = 1;
const MAX_EVIDENCES_PER_SECTION = 6;

const portraitEvidenceSchema = z.object({
  entity: z.object({
    kind: z.enum(['planet', 'house', 'sign', 'aspect']),
    id: z.string(),
    label: z.string(),
  }),
  technicalValue: z.string(),
  relation: z.string(),
  explanation: z.string().min(MIN_EVIDENCE_EXPLANATION_LENGTH),
});

const portraitSectionSchema = z.object({
  title: z.string(),
  body: z.string().min(MIN_SECTION_TEXT_LENGTH),
  everydayExample: z.string(),
  factors: z.array(z.string()).min(MIN_FACTORS_COUNT).max(MAX_FACTORS_COUNT),
  evidences: z
    .array(portraitEvidenceSchema)
    .min(MIN_EVIDENCES_PER_SECTION)
    .max(MAX_EVIDENCES_PER_SECTION),
  oraclePrompt: z.string(),
});

export const PORTRAIT_SECTION_KEYS = [
  'centralAxis',
  'decisionMaking',
  'emotionalWorld',
  'connection',
  'naturalStrengths',
  'internalTensions',
  'underPressure',
  'integration',
] as const;

export type PortraitSectionKey = (typeof PORTRAIT_SECTION_KEYS)[number];

export const PersonalPortraitSchema = z.object({
  centralAxis: portraitSectionSchema,
  decisionMaking: portraitSectionSchema,
  emotionalWorld: portraitSectionSchema,
  connection: portraitSectionSchema,
  naturalStrengths: portraitSectionSchema,
  internalTensions: portraitSectionSchema,
  underPressure: portraitSectionSchema,
  integration: portraitSectionSchema,
});

export type PersonalPortrait = z.infer<typeof PersonalPortraitSchema>;

export const PORTRAIT_LANGUAGE_ENUM = z.enum(['pt', 'en', 'es']);

// --- Personal Portrait Long (30+ min read, 12 sections) ---

export const PORTRAIT_LONG_SECTION_KEYS = [
  'centralAxis',
  'decisionMaking',
  'emotionalWorld',
  'connection',
  'naturalStrengths',
  'internalTensions',
  'underPressure',
  'shadowWork',
  'creativityExpression',
  'spiritualInquiry',
  'lifeCycles',
  'integration',
] as const;

export type PortraitLongSectionKey = (typeof PORTRAIT_LONG_SECTION_KEYS)[number];

export const PersonalPortraitLongSchema = z.object({
  centralAxis: portraitSectionSchema,
  decisionMaking: portraitSectionSchema,
  emotionalWorld: portraitSectionSchema,
  connection: portraitSectionSchema,
  naturalStrengths: portraitSectionSchema,
  internalTensions: portraitSectionSchema,
  underPressure: portraitSectionSchema,
  shadowWork: portraitSectionSchema,
  creativityExpression: portraitSectionSchema,
  spiritualInquiry: portraitSectionSchema,
  lifeCycles: portraitSectionSchema,
  integration: portraitSectionSchema,
});

export type PersonalPortraitLong = z.infer<typeof PersonalPortraitLongSchema>;
export type PortraitLanguage = z.infer<typeof PORTRAIT_LANGUAGE_ENUM>;

// --- Daily Timeline ---

const timelineEntrySchema = z.object({
  date: z.string(),
  intensity: z.enum(['low', 'medium', 'high']),
  title: z.string(),
  description: z.string(),
  transitAspect: z
    .object({
      transitPlanet: z.string(),
      aspect: z.string(),
      natalPlanet: z.string(),
    })
    .optional(),
});

export const DailyTimelineSchema = z.object({
  entries: z.array(timelineEntrySchema).min(1),
  monthlyTheme: z.string(),
  monthlySummary: z.string(),
});

export type DailyTimeline = z.infer<typeof DailyTimelineSchema>;

// --- Thematic Reports ---

export const THEMATIC_REPORT_TYPES = [
  'career',
  'love',
  'health',
  'money',
  'relationships',
] as const;

export type ThematicReportType = (typeof THEMATIC_REPORT_TYPES)[number];

export const ThematicReportSchema = z.object({
  title: z.string(),
  body: z.string().min(120),
  everydayExample: z.string(),
  factors: z.array(z.string()).min(1).max(8),
  evidences: z
    .array(portraitEvidenceSchema)
    .min(MIN_EVIDENCES_PER_SECTION)
    .max(MAX_EVIDENCES_PER_SECTION),
  oraclePrompt: z.string(),
});

export type ThematicReport = z.infer<typeof ThematicReportSchema>;

// --- Synastry Editorial Report ---

export const SynastryEditorialSchema = z.object({
  title: z.string(),
  overallDynamics: z.string().min(120),
  strengths: z.array(z.string()).min(1),
  challenges: z.array(z.string()).min(1),
  comparativeEvidence: z
    .array(portraitEvidenceSchema)
    .min(1)
    .max(MAX_EVIDENCES_PER_SECTION),
  oraclePrompt: z.string(),
  limitations: z.string(),
});

export type SynastryEditorial = z.infer<typeof SynastryEditorialSchema>;
